import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { X, Globe } from 'lucide-react';
import { Button } from '../ui/button';

const LanguageBanner = () => {
  const { language, setLanguage, languages, t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState(null);

  // Language names in their native form for the banner
  const languageNativeNames = {
    de: 'Deutsch',
    en: 'English',
    fr: 'Français',
    es: 'Español',
    ar: 'العربية',
    zh: '中文',
    hi: 'हिन्दी',
    id: 'Bahasa Indonesia',
    ru: 'Русский',
    pt: 'Português',
    vi: 'Tiếng Việt',
    it: 'Italiano',
    nl: 'Nederlands',
    ja: '日本語',
    ko: '한국어',
    tr: 'Türkçe',
    pl: 'Polski',
    sv: 'Svenska',
    th: 'ไทย',
    he: 'עברית',
    uk: 'Українська',
    el: 'Ελληνικά',
    cs: 'Čeština',
    ro: 'Română',
    hu: 'Magyar',
    da: 'Dansk',
    no: 'Norsk',
    fi: 'Suomi',
    ms: 'Bahasa Melayu',
    bn: 'বাংলা',
    sw: 'Kiswahili',
    ur: 'اردو',
    ta: 'தமிழ்',
    te: 'తెలుగు',
  };

  // Banner messages in different languages
  const bannerMessages = {
    de: (lang) => `Wir haben erkannt, dass Sie ${languageNativeNames[lang]} sprechen.`,
    en: (lang) => `We detected that you speak ${languageNativeNames[lang]}.`,
    fr: (lang) => `Nous avons détecté que vous parlez ${languageNativeNames[lang]}.`,
    es: (lang) => `Detectamos que hablas ${languageNativeNames[lang]}.`,
    ar: (lang) => `لقد كشفنا أنك تتكلم ${languageNativeNames[lang]}.`,
    zh: (lang) => `我们检测到您使用${languageNativeNames[lang]}。`,
    hi: (lang) => `हमने पाया कि आप ${languageNativeNames[lang]} बोलते हैं।`,
    id: (lang) => `Kami mendeteksi bahwa Anda berbicara ${languageNativeNames[lang]}.`,
    ru: (lang) => `Мы обнаружили, что вы говорите на ${languageNativeNames[lang]}.`,
    pt: (lang) => `Detectamos que você fala ${languageNativeNames[lang]}.`,
    vi: (lang) => `Chúng tôi phát hiện bạn nói ${languageNativeNames[lang]}.`,
    it: (lang) => `Abbiamo rilevato che parla ${languageNativeNames[lang]}.`,
    nl: (lang) => `We hebben gedetecteerd dat u ${languageNativeNames[lang]} spreekt.`,
    ja: (lang) => `${languageNativeNames[lang]} をお話しだと判明しました。`,
    ko: (lang) => `${languageNativeNames[lang]}(을)를 사용한다고 감지되었습니다.`,
    tr: (lang) => `${languageNativeNames[lang]} konuşduğunuzu tespit ettik.`,
    pl: (lang) => `Wykryliśmy, że mówisz po ${languageNativeNames[lang]}.`,
    sv: (lang) => `Vi har uppmätt att du talar ${languageNativeNames[lang]}.`,
    th: (lang) => `ตรวจพบว่าคุณพูด${languageNativeNames[lang]}`,
    he: (lang) => `איתרנו שאתה מדבר ב${languageNativeNames[lang]}.`,
    uk: (lang) => `Ми виявили, що ви говорите ${languageNativeNames[lang]}.`,
    el: (lang) => `Ανιχνεύσαμε ότι μιλάτε ${languageNativeNames[lang]}.`,
    cs: (lang) => `Zjistili jsme, že mluvíš ${languageNativeNames[lang]}.`,
    ro: (lang) => `Am detectat că vorbești ${languageNativeNames[lang]}.`,
    hu: (lang) => `Megállapítottuk, hogy ${languageNativeNames[lang]} nyelven beszélsz.`,
    da: (lang) => `Vi har opdaget, at du taler ${languageNativeNames[lang]}.`,
    no: (lang) => `Vi har oppdaget at du snakker ${languageNativeNames[lang]}.`,
    fi: (lang) => `Havaitsimme, että puhut ${languageNativeNames[lang]}.`,
    ms: (lang) => `Kami mendapati anda bercakap ${languageNativeNames[lang]}.`,
    bn: (lang) => `আমরা পর্যবেক্ষণ করেছি যে তুমি ${languageNativeNames[lang]} বলেন।`,
    sw: (lang) => `Tumetambua kwamba unazungumza ${languageNativeNames[lang]}.`,
    ur: (lang) => `ہم نے دریافت کیا ہے کہ آپ ${languageNativeNames[lang]} بولتے ہیں۔`,
    ta: (lang) => `நாம் கண்டறிந்தோம் நீங்கள் ${languageNativeNames[lang]} பேசுகிறார்கள்.`,
    te: (lang) => `${languageNativeNames[lang]} మాట్లాడుతున్నారని గుర్తించాం.`,
  };

  const switchButtonText = {
    de: 'Sprache wechseln',
    en: 'Switch Language',
    fr: 'Changer de langue',
    es: 'Cambiar idioma',
    ar: 'تغيير اللغة',
    zh: '切换语言',
    hi: 'भाषा बदलें',
    id: 'Ganti Bahasa',
    ru: 'Сменить язык',
    pt: 'Mudar idioma',
    vi: 'Đổi ngôn ngữ',
    it: 'Cambia lingua',
    nl: 'Taal wijzigen',
    ja: '言語を変更',
    ko: '언어 변경',
    tr: 'Dil Değiştir',
    pl: 'Zmień język',
    sv: 'Byt språk',
    th: 'เลือกภาษา',
    he: 'החלפת שפה',
    uk: 'Змінити мову',
    el: 'Αλλαγή γλώσσας',
    cs: 'Změnit jazyk',
    ro: 'Schimbă limba',
    hu: 'Nyelv váltás',
    da: 'Skift sprog',
    no: 'Bytt språk',
    fi: 'Vaihda kieltä',
    ms: 'Tukar Bahasa',
    bn: 'ভাষা পরিবর্তন',
    sw: 'Badili Lugha',
    ur: 'زبان تبدیل کریں',
    ta: 'மொழியை மாற்ற வேண்டும்',
    te: 'భాష మార్చాలి',
  };

  const dismissText = {
    de: 'Nein, danke',
    en: 'No, thanks',
    fr: 'Non, merci',
    es: 'No, gracias',
    ar: 'لا شكرا',
    zh: '不用了',
    hi: 'नहीं धन्यवाद',
    id: 'Tidak, terima kasih',
    ru: 'Нет, спасибо',
    pt: 'Não, obrigado',
    vi: 'Không, cảm ơn',
    it: 'No, grazie',
    nl: 'Nee, dank u',
    ja: 'いいえ、結構です',
    ko: '아니오, 감사합니다',
    tr: 'Hayır, teşekkürler',
    pl: 'Nie, dziękuję',
    sv: 'Nej, tack',
    th: 'ไม่, ขอบคุณ',
    he: 'לא, תודה',
    uk: 'Ні, дякую',
    el: 'Όχι, ευχαριστώ',
    cs: 'Ne, děkuji',
    ro: 'Nu, mulțumesc',
    hu: 'Nem, köszönöm',
    da: 'Nej, tak',
    no: 'Nei, takk',
    fi: 'Ei kiitos',
    ms: 'Tidak, terima kasih',
    bn: 'না, ধন্যবাদ',
    sw: 'Hapana, asante',
    ur: 'نہیں، شکریہ',
    ta: 'இல்லை, நன்றி',
    te: 'కాదు, ధన్యవాదాలు',
  };

  useEffect(() => {
    // Check if user has already dismissed or accepted the banner
    const bannerDismissed = localStorage.getItem('wedvaya_language_banner_dismissed');
    if (bannerDismissed) {
      return;
    }

    // Detect browser language
    const browserLang = navigator.language.split('-')[0];

    // Check if browser language is supported and different from current
    const supportedLang = languages.find(l => l.code === browserLang);

    if (supportedLang && browserLang !== language) {
      setDetectedLanguage(browserLang);
      setShowBanner(true);
    }
  }, [language, languages]);

  const handleSwitchLanguage = () => {
    if (detectedLanguage) {
      setLanguage(detectedLanguage);
      localStorage.setItem('wedvaya_language_banner_dismissed', 'true');
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('wedvaya_language_banner_dismissed', 'true');
    setShowBanner(false);
  };

  if (!showBanner || !detectedLanguage) {
    return null;
  }

  const getMessage = bannerMessages[detectedLanguage] || bannerMessages.en;
  const buttonText = switchButtonText[detectedLanguage] || switchButtonText.en;
  const dismissLabel = dismissText[language] || dismissText.en;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="language-banner"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-primary/20 p-4 flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
          <Globe className="h-5 w-5 text-primary" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground font-medium mb-2">
            {getMessage(detectedLanguage)}
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-4"
              onClick={handleSwitchLanguage}
              data-testid="switch-language-btn"
            >
              {buttonText}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
              onClick={handleDismiss}
              data-testid="dismiss-banner-btn"
            >
              {dismissLabel}
            </Button>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default LanguageBanner;